import * as React from "react"
import OutlinedInput, { type OutlinedInputProps } from "@mui/material/OutlinedInput"

import { cn } from "@/lib/utils"

type InputProps = Omit<React.ComponentProps<"input">, "size"> & {
  size?: string | number
  inputProps?: OutlinedInputProps["inputProps"]
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      type = "text",
      size,
      inputProps,
      maxLength,
      inputMode,
      autoComplete,
      autoCapitalize,
      autoCorrect,
      spellCheck,
      enterKeyHint,
      pattern,
      ...props
    },
    ref,
  ) => {
    const muiInputProps = props as Omit<
      OutlinedInputProps,
      | "className"
      | "fullWidth"
      | "inputRef"
      | "sx"
      | "type"
      | "inputProps"
    >

    return (
      <OutlinedInput
        fullWidth
        inputRef={ref}
        type={type}
        inputProps={{
          ...(typeof size === "number" ? { size } : null),
          ...(maxLength != null ? { maxLength } : null),
          ...(inputMode != null ? { inputMode } : null),
          ...(autoComplete != null ? { autoComplete } : null),
          ...(autoCapitalize != null ? { autoCapitalize } : null),
          ...(autoCorrect != null ? { autoCorrect } : null),
          ...(spellCheck != null ? { spellCheck } : null),
          ...(enterKeyHint != null ? { enterKeyHint } : null),
          ...(pattern != null ? { pattern } : null),
          ...inputProps,
        }}
        className={cn(
          "flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-base placeholder:text-muted-foreground md:text-sm",
          "outline-none transition-colors focus-within:!border-ring",
          "[&_input[type=number]::-webkit-outer-spin-button]:appearance-none [&_input[type=number]::-webkit-inner-spin-button]:appearance-none [&_input[type=number]]:appearance-none [&_input[type=number]]:[-moz-appearance:textfield]",
          className
        )}
        sx={{
          // OutlinedInput already draws a border via notched outline; hide it so
          // Tailwind `border border-input` is the only visible edge (avoids double line).
          "& .MuiOutlinedInput-notchedOutline": { border: "none" },
          "&:hover .MuiOutlinedInput-notchedOutline": { border: "none" },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": { border: "none" },
          "& .MuiOutlinedInput-input": {
            padding: "8px 12px",
            outline: "none",
          },
          // Undo Chrome autofill / suggestion tint so fill matches `bg-background`.
          "& .MuiOutlinedInput-input:-webkit-autofill": {
            WebkitBoxShadow: "0 0 0 1000px hsl(var(--background)) inset",
            WebkitTextFillColor: "hsl(var(--foreground))",
            caretColor: "hsl(var(--foreground))",
            transition: "background-color 5000s ease-in-out 0s",
          },
          "& .MuiOutlinedInput-input:-webkit-autofill:hover": {
            WebkitBoxShadow: "0 0 0 1000px hsl(var(--background)) inset",
            WebkitTextFillColor: "hsl(var(--foreground))",
          },
          "& .MuiOutlinedInput-input:-webkit-autofill:focus": {
            WebkitBoxShadow: "0 0 0 1000px hsl(var(--background)) inset",
            WebkitTextFillColor: "hsl(var(--foreground))",
          },
          "& .MuiOutlinedInput-input:-webkit-autofill:active": {
            WebkitBoxShadow: "0 0 0 1000px hsl(var(--background)) inset",
            WebkitTextFillColor: "hsl(var(--foreground))",
          },
          "&.Mui-disabled": {
            opacity: 0.5,
          },
        }}
        {...muiInputProps}
      />
    )
  }
)
Input.displayName = "Input"

export { Input }
